import styles from './WaveStatsBar.module.css';
import ProgressBar from '../../../../../ui/ProgressBar/ProgressBar';
import cn from 'classnames';
import { asMoney, formatDate, formatNumberToKNotation, roundTo2Dp, showNotification } from 'helpers';
import { wave, WaveInfluencerPostLiveStats } from '@round/api';
import { ReactComponent as ArrowIcon } from '../../../../../assets/ArrowIcon.svg';
import React, { useContext } from 'react';
import { InfluencerPlanContext } from '../../contexts/InfluencerPlanContext';
import { getPostCountDisplayValue } from 'Modules/Advertising/InfluencerPlan/features/MicrosStatsBar/helpers';
import { ProtectedByUserGroups } from 'SharedComponents/ProtectedByUserGroup/ProtectedByUserGroups';
import { useLinkedProject } from '../Creators/components/CreatorbaseProjectSelect/hooks/useLinkedProject';
import useCreatorbaseProjectNavigationLink from '../Creators/useCreatorbaseNavigationLink';
import ShareWithCreatorbaseToggle from '../InfluencerPlanGroupBar/ShareWithCreatorbaseToggle';
import CountBadge from 'ui-new/Badges/CountBadge/CountBadge';

type WaveStatsBarProps = {
    campaign: WaveInfluencerPostLiveStats;
    isExpanded: boolean;
    onExpandedChange: (expanded: boolean) => void;
    // Called after the share toggle patches the campaign, so the bar can refetch.
    onCampaignUpdated?: () => void | Promise<void>;
    children?: React.ReactNode | undefined;
};

const WaveStatsBar = ({ campaign, isExpanded, onExpandedChange, onCampaignUpdated, children }: WaveStatsBarProps) => {
    const { influencerPlan } = useContext(InfluencerPlanContext);

    const { linkedProject } = useLinkedProject(influencerPlan?.application_manager_project_id);
    const { creatorbaseProject } = useCreatorbaseProjectNavigationLink(influencerPlan?.application_manager_project_id);
    const hasAccessToCreatorbaseProject = !!linkedProject || !!creatorbaseProject;

    const postCountDisplayValue = getPostCountDisplayValue(campaign.post_count, campaign.post_count_target);
    const budgetDisplayValue = asMoney(campaign.budget, influencerPlan?.currency);

    // Wave's amount_spent is the approved budget, which can exceed budget, so distinguish 0 from
    // "no budget" and clamp the bar (unlike Micros, whose spend is capped at budget).
    const amountSpentPercent = campaign.budget > 0 ? (campaign.amount_spent / campaign.budget) * 100 : null;
    const amountSpentPercentDisplayValue = amountSpentPercent === null ? '-' : `${amountSpentPercent.toFixed(2)}%`;

    const engagementRate = campaign.engagement_rate === null ? '-' : `${roundTo2Dp(campaign.engagement_rate * 100)}%`;

    const totalViews = typeof campaign.total_views === 'number' ? formatNumberToKNotation(campaign.total_views) : '-';

    const cpm = typeof campaign.cpm === 'number' ? asMoney(campaign.cpm, influencerPlan?.currency) : '-';

    const deadline = campaign.deadline ? formatDate(campaign.deadline) : '-';

    return (
        <>
            <section className={styles.container}>
                <button
                    className={cn(styles.expandButton, { [styles.expanded]: isExpanded })}
                    onClick={() => onExpandedChange(!isExpanded)}
                >
                    <ArrowIcon />
                </button>
                <div className={styles.statsContainer}>
                    <div className={cn(styles.statColumn, styles.name)}>
                        <div className={styles.statHeading}>{campaign.name}</div>
                        <div className={styles.statValue}>{postCountDisplayValue}</div>
                    </div>

                    <div className={cn(styles.statColumn, styles.costStats)}>
                        <div className={styles.budgetRow}>
                            <div className={cn(styles.statHeading, styles.budget)}>{budgetDisplayValue}</div>
                            <div className={styles.statValue}>{amountSpentPercentDisplayValue}</div>
                        </div>

                        <ProgressBar
                            trackStyles={{
                                width: '100%',
                                backgroundColor: 'rgba(255, 255, 255, 0.4)',
                                height: '0.33rem',
                                marginTop: '0.33rem',
                            }}
                            barStyles={{
                                backgroundColor: 'white',
                            }}
                            progress={Math.min(Math.max(amountSpentPercent ?? 0, 0), 100)}
                        />
                    </div>

                    <div className={cn(styles.statColumn, styles.views)}>
                        <div className={styles.statHeading}>Total Views</div>
                        <div className={styles.statValue}>{totalViews}</div>
                    </div>

                    <div className={cn(styles.statColumn, styles.engagementRate)}>
                        <div className={styles.statHeading}>Engagement Rate</div>
                        <div className={styles.statValue}>{engagementRate}</div>
                    </div>

                    <div className={styles.statColumn}>
                        <div className={styles.statHeading}>CPM</div>
                        <div className={styles.statValue}>{cpm}</div>
                    </div>

                    <div className={styles.statColumn}>
                        <div className={styles.statHeading}>Deadline</div>
                        <div className={styles.statValue}>{deadline}</div>
                    </div>

                    <div className={styles.statColumn}>
                        <div className={styles.statHeading}>To approve</div>
                        <div className={styles.statValue}>
                            <CountBadge
                                count={campaign.pending_review_count}
                                className={cn({ [styles.alert]: campaign.pending_review_count > 0 })}
                            />
                        </div>
                    </div>

                    {hasAccessToCreatorbaseProject && (
                        <ProtectedByUserGroups groups={['user_admin']}>
                            <ShareWithCreatorbaseToggle
                                isShared={campaign.is_shared_with_creatorbase}
                                onChange={async (isShared) => {
                                    try {
                                        const response = await wave.patchCampaign(campaign.campaign_id, {
                                            is_shared_with_creatorbase: isShared,
                                        });
                                        if (response.status === 200) {
                                            showNotification('Success', 'info');
                                            await onCampaignUpdated?.();
                                            return;
                                        }

                                        if (response.status === 400) {
                                            showNotification(
                                                Array.isArray(response.data)
                                                    ? String(response.data)
                                                    : String(response.data.is_shared_with_creatorbase),
                                                'error'
                                            );
                                            return;
                                        }

                                        showNotification(response.data.detail, 'error');
                                    } catch {
                                        showNotification('Could not share campaign', 'error');
                                    }
                                }}
                            />
                        </ProtectedByUserGroups>
                    )}
                </div>
            </section>
            {isExpanded && children}
        </>
    );
};

export default WaveStatsBar;
